import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiFilterRequest, ApiFiltersRequest, InternalApiPageableResponse } from '../api';
import { ApiPageableRequest } from '../api/request/api-pageable-request';
import { TablePage } from '../models';
import { AbstractDataSource } from './abstract-data-source';

/**
 * Function type for the API function.
 *
 */
export declare type ListApiFunction<T> = (filter: ApiFiltersRequest) => Observable<InternalApiPageableResponse<T>>;

/**
 * Constructor options for the DataSource class.
 *
 */
interface DataSourceConstructor<T> {
    clientSideManager?: boolean;
    tablePage?: TablePage;
    observableFn: ListApiFunction<T>;
    filters?: ApiFilterRequest[];
}

/**
 * Data source for fetching and managing data from the API.
 *
 */
export class DataSource<T> extends AbstractDataSource<T> {
    /**
     * Override of the observable function.
     *
     */
    protected override observable!: ListApiFunction<T>;
    protected tmpRows: T[] = [];

    /**
     * Creates an instance of the DataSource class.
     * @param options - Constructor options for the data source.
     *
     */
    constructor(options: DataSourceConstructor<T>) {
        super(options);
        this.tablePage = options.tablePage || new TablePage({ pageNumber: 0, size: 10 });
        this.initialPage = this.tablePage.pageNumber;
    }

    /**
     * Retrieves the elements from the API.
     * @returns An Observable that emits the array of elements.
     *
     */
    override getElements(): Observable<T[]> {
        if (this._clientSideManager && !this._firstAPICall) {
            return this._getOnLocal();
        }
        const filterRequest = new ApiFiltersRequest({
            filters: this.filter,
            page: new ApiPageableRequest({
                pageNumber: this.tablePage.pageNumber,
                pageSize: this.tablePage.size,
                sort: this.tableSort.map((sort) => ({
                    direction: sort.dir === 'asc' ? 'ASC' : 'DESC',
                    field: sort.prop
                }))
            })
        });

        return this.observable(filterRequest).pipe(
            map((res: InternalApiPageableResponse<T>) => {
                this.tablePage.totalElements = res?.page?.totalElements || 0;
                this.tablePage.totalPages = res?.page?.totalPages ?? 0;
                this.tmpRows = res.content;
                return res.content;
            })
        );
    }

    private _sliceRows(rows: T[]): T[] {
        const start = this.tablePage.pageNumber * this.tablePage.size;
        const end = start + this.tablePage.size;

        return rows.slice(start, end);
    }

    private _calcTotalPages(totalElements: number): number {
        return Math.floor((totalElements || 0) / this.tablePage.size) || 1;
    }

    private _getOnLocal(): Observable<T[]> {
        this.tablePage.totalPages = this._calcTotalPages(this.tmpRows.length);
        if (this.filter.length) {
            const rows: T[] = [];
            this.tmpRows.forEach((row) => {
                if (
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    this.filter.some((f) => row[f.field].search(f.value) > -1)
                ) {
                    rows.push(row);
                }
            });
            return of(this._sliceRows(rows));
        }
        return of(this._sliceRows(this.tmpRows));
    }
}
